import axios from 'axios';
import { useAuth } from '../../hooks/use-auth';
import { useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { Fragment, useState } from 'react';
import prefectures from 'components/PrefecturesOption';
import config from 'config';
import { removeKeyWithValue } from 'utils';
import { CommonWrapper, CommonTitle, CommonLabel, CommonInput, CommonSubmit, CommonFormWrapper, CommonFormTitle, FormLabel, FormSelect, FormInput, FormLabelInputWrapper, LoadingContainer } from 'styles/styles';
import { CircularProgress } from '@mui/material';

const SignUpForm = () => {

	interface Inputs {
		email: string,
		password: string,
	};

	const auth = useAuth();
	const [isSubmitDisable, setIsSubmitDisable] = useState<boolean>(false);
	const { register, handleSubmit, watch, formState: { errors } } = useForm<Inputs>();

	const onSubmit: SubmitHandler<Inputs> = async data => {
		setIsSubmitDisable(true);
		const res: any = await auth.signUp(data.email, data.password);
		/* error処理 */
		setIsSubmitDisable(false);
		if (!res.success) {
			window.alert(res.message);
		} else {
			window.alert("登録が完了しました。");
		}
	};

	return (
		<>
			<form onSubmit={handleSubmit(onSubmit)}>
				<CommonWrapper>
					<CommonTitle>新規アカウント発行</CommonTitle>
					<CommonLabel htmlFor="email">メールアドレス</CommonLabel>
					<CommonInput {...register("email")} type="email" id="email" name="email" placeholder='メールアドレス' required />
					<CommonLabel htmlFor="password">パスワード</CommonLabel>
					<CommonInput {...register("password")} type="password" id="password" name="password" placeholder='パスワード' required />
					{isSubmitDisable ? (
						<LoadingContainer>
							<CircularProgress size={20} />
						</LoadingContainer>
					) : (
						<CommonSubmit type="submit" disabled={isSubmitDisable} />
					)}
				</CommonWrapper>
			</form>
		</>
	)
};


const ConfirmForm = () => {
	interface Inputs {
		verificationCode: string,
	};

	const auth = useAuth();
	const { register, handleSubmit, watch, formState: { errors } } = useForm<Inputs>();

	const onSubmit: SubmitHandler<Inputs> = async data => {
		auth.confirmSignUp(data.verificationCode);
	};

	return (
		<>
			<form onSubmit={handleSubmit(onSubmit)}>
				<CommonWrapper>
					<CommonTitle>認証コード入力</CommonTitle>
					<CommonLabel htmlFor="verificationCode">認証コード</CommonLabel>
					<CommonInput {...register("verificationCode")} type="verificationCode" id="verificationCode" name="verificationCode" required />
					<CommonSubmit type="submit" value="送信" />
				</CommonWrapper>
			</form>
		</>
	)
}

const ThanksForm = () => {

	interface ParentDataProps {
		company_name: string,
		company_name_kana: string,
		company_postcode: number,
		company_prefecture_id: number,
		company_city: string,
		company_block: string,
		company_building: string,
		company_phone_number: string,
		factory_name: string,
		factory_name_kana: string,
		factory_postcode: number,
		factory_prefecture_id: number,
		factory_city: string,
		factory_block: string,
		factory_building: string,
		factory_phone_number: string,
		factory_display_id: string,
		[key: string]: string | number;
	}

	const auth = useAuth();
	const [isSubmitDisable, setIsSubmitDisable] = useState<boolean>(false);
	const { reset, register, handleSubmit, watch, formState: { errors } } = useForm<ParentDataProps>();

	const navigate = useNavigate();

	const onSubmit: SubmitHandler<ParentDataProps> = async (submitData: ParentDataProps) => {
		try {
			setIsSubmitDisable(true);
			submitData = removeKeyWithValue(submitData, '');

			// SignUp時に暫定的なcompanyIdが作成されるので、ここではpatch
			await axios.patch(`/v0/account/companies`, {
				name: submitData.company_name,
				name_kana: submitData.company_name_kana,
				postcode: submitData.company_postcode,
				prefecture_id: submitData.company_prefecture_id,
				city: submitData.company_city,
				block: submitData.company_block,
				building: submitData.company_building,
				phone_number: submitData.company_phone_number
			}, {
				headers: auth.authorizationHeader
			});
			await axios.patch(`/v0/account/factories`, {
				company_id: auth.companyId,
				name: submitData.factory_name,
				name_kana: submitData.factory_name_kana,
				postcode: submitData.factory_postcode,
				prefecture_id: submitData.factory_prefecture_id,
				city: submitData.factory_city,
				block: submitData.factory_block,
				building: submitData.factory_building,
				phone_number: submitData.factory_phone_number,
				display_id: submitData.factory_display_id,
			}, {
				headers: auth.authorizationHeader,
				params: { factory_id: auth.factoryId }
			});

			window.alert("ユーザーの登録が完了しました。続いて、ユーザーページより必須事項の入力をお願いします。");
			navigate("/f0/user");
		} catch (error: any) {
			window.alert(error.response.data.error);
		}
		setIsSubmitDisable(false);
	}

	const companyArray = [
		{ title: '法人名', field: 'company_name', type: 'text', required: true },
		{ title: 'カナ', field: 'company_name_kana', type: 'text' },
		{ title: '郵便番号', field: 'company_postcode', type: 'number' },
		{ title: '都道府県', field: 'company_prefecture_id', type: 'text', select: 'prefecture' },
		{ title: '市区町村', field: 'company_city', type: 'text' },
		{ title: '番地', field: 'company_block', type: 'text' },
		{ title: '建物名以下', field: 'company_building', type: 'text' },
		{ title: '電話番号', field: 'company_phone_number', type: 'tel' },
	]

	const companyItems = companyArray.map((element: any, index) => {
		if (element.title) {
			return (
				<Fragment key={index}>
					<FormLabelInputWrapper>
						<FormLabel htmlFor={element.field}>{element.title}</FormLabel>
						{element.select === "prefecture" ?
							<FormSelect {...register("company_prefecture_id")}>
								<option hidden value={""}>都道府県を選択してください</option>
								{prefectures.map((prefecture) => (
									<option key={prefecture.code} value={prefecture.code}>
										{prefecture.name}
									</option>
								))}
							</FormSelect> :
							<FormInput {...register(element.field)}
								{...(element.type && { type: config.inputErrorMessage[element.type].type })}
								id={element.field}
								{...(element.type && { placeholder: config.inputErrorMessage[element.type].placeholder })}
								{...(element.type && { pattern: config.inputErrorMessage[element.type].pattern })}
								{...(element.type && { title: config.inputErrorMessage[element.type].title })}
								{...(element.required && { required: true })}
								{...(element.disable && { disabled: true })}
								{...(element.type && { maxLength: config.inputErrorMessage[element.type].maxLength })}
								{...(element.type === "number" && { max: config.inputErrorMessage[element.type].max })}
								{...(element.field.includes("postcode") && { max: 9999999 })}
							/>}
					</FormLabelInputWrapper>
				</Fragment>
			)
		}
	});

	const factoryArray = [
		{ title: '工場ID', field: 'factory_display_id', type: 'text' },
		{ title: '工場名', field: 'factory_name', type: 'text', required: true },
		{ title: 'カナ', field: 'factory_name_kana', type: 'text' },
		{ title: '郵便番号', field: 'factory_postcode', type: 'number' },
		{ title: '都道府県', field: 'factory_prefecture_id', type: 'number', select: 'prefecture' },
		{ title: '市区町村', field: 'factory_city', type: 'text' },
		{ title: '番地', field: 'factory_block', type: 'text' },
		{ title: '建物名以下', field: 'factory_building', type: 'text' },
		{ title: '電話番号', field: 'factory_phone_number', type: 'tel' },
	]

	const factoryItems = factoryArray.map((element: any, index) => {
		if (element.title) {
			return (
				<Fragment key={index}>
					<FormLabelInputWrapper>
						<FormLabel htmlFor={element.field}>{element.title}</FormLabel>
						{element.select === "prefecture" ?
							<FormSelect {...register("factory_prefecture_id")}>
								<option hidden value={""}>都道府県を選択してください</option>
								{prefectures.map((prefecture) => (
									<option key={prefecture.code} value={prefecture.code}>
										{prefecture.name}
									</option>
								))}
							</FormSelect> :
							<FormInput {...register(element.field)}
								{...(element.type && { type: config.inputErrorMessage[element.type].type })}
								id={element.field}
								{...(element.type && { placeholder: config.inputErrorMessage[element.type].placeholder })}
								{...(element.type && { pattern: config.inputErrorMessage[element.type].pattern })}
								{...(element.type && { title: config.inputErrorMessage[element.type].title })}
								{...(element.required && { required: true })}
								{...(element.disable && { disabled: true })}
								{...(element.type && { maxLength: config.inputErrorMessage[element.type].maxLength })}
								{...(element.type === "number" && { max: config.inputErrorMessage[element.type].max })}
								{...(element.field.includes("postcode") && { max: 9999999 })}
							/>}
					</FormLabelInputWrapper>
				</Fragment>
			)
		}
	});


	return (
		<>
			<CommonFormWrapper onSubmit={handleSubmit(onSubmit)}>
				<CommonTitle>新規アカウント作成</CommonTitle>
				<CommonFormTitle>法人情報を登録</CommonFormTitle>
				{companyItems}
				<CommonFormTitle>工場情報を登録</CommonFormTitle>
				{factoryItems}
				{isSubmitDisable ? (
					<LoadingContainer>
						<CircularProgress size={20} />
					</LoadingContainer>
				) : (
					<CommonSubmit type="submit" disabled={isSubmitDisable} />
				)}
			</CommonFormWrapper>
		</>
	)
}

const SignUp = () => {
	const auth = useAuth();
	if (auth.isAuthenticated) {
		return <ThanksForm />;
	}
	if (!auth.email) {
		return <SignUpForm />;
	} else if (!auth.isAuthenticated) {
		return <ConfirmForm />;
	}
	return <></>
};

export default SignUp;